/**
 * T-046: AASA file preview + bundle IDs.
 *
 * Read-only. Edits to the file (bundle IDs, paths) are deferred to a
 * follow-up ticket once the AASA serving moves from build-time-static
 * to a runtime endpoint.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import type { PocketAasaInfo } from '@/types/pocketSettings';

interface Props {
  info: PocketAasaInfo;
  appDomain: string;
}

export function AasaPreviewCard({ info, appDomain }: Props) {
  const validatorUrl = `https://app-site-association.cdn-apple.com/a/v1/${encodeURIComponent(appDomain)}`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base font-semibold">
            apple-app-site-association
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1 break-all">{info.served_url}</p>
        </div>
        <Button asChild size="sm" variant="outline">
          <a href={validatorUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Apple validator
          </a>
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="text-sm space-y-1.5 grid grid-cols-[auto_1fr] gap-x-4">
          <dt className="text-muted-foreground">Last modified</dt>
          <dd className="tabular-nums">
            {info.last_modified ? info.last_modified : '—'}
          </dd>
          <dt className="text-muted-foreground">Bundle IDs</dt>
          <dd className="space-x-1.5">
            {info.bundle_ids.length === 0 ? (
              <span className="text-muted-foreground">none registered</span>
            ) : (
              info.bundle_ids.map((id) => (
                <Badge key={id} variant="secondary" className="font-mono">
                  {id}
                </Badge>
              ))
            )}
          </dd>
        </dl>

        {info.fetch_error ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <strong>Fetch error:</strong> {info.fetch_error}
          </div>
        ) : null}

        <details>
          <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground">
            View raw JSON
          </summary>
          <pre className="mt-2 p-3 rounded-md bg-muted text-xs overflow-auto max-h-80 font-mono">
            {JSON.stringify(info.content, null, 2)}
          </pre>
        </details>
      </CardContent>
    </Card>
  );
}
